/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createLogger } from '@/lib/logger';

const logger = createLogger();

/**
 * RGBARRY-CUSTOMIZATION (DRAFT — W-23789028 consent rework): a tiny client-side store for the
 * shopper's current TrueVault opt-out state.
 *
 * WHY THIS EXISTS: when TrueVault is the CMP, the Salesforce server-side engagement adapters
 * (einstein / data360 / activeData) fire as server HTTP calls with NO page script tag, so TrueVault's
 * site-scanner cannot see or block them. The solid path is to gate them explicitly on the shopper's
 * opt-out. Those adapters are dispatched client-side through `useAnalytics` / `PageViewTracker`, which
 * gate every event on the engagement `consentPreferences`; this store is the signal they read to block
 * those adapters the moment the shopper opts out (see `src/hooks/use-analytics.ts` and
 * `src/analytics/page-view-tracker.tsx`).
 *
 * SIGNAL SOURCE — the `polaris_consent_settings` cookie (initial), `consentChanged` (in-session).
 * Per TrueVault Engineering (2026-08-20, plan doc §10, answer 2.2): `polaris_consent_settings` is a
 * FIRST-PARTY cookie on the root domain (`path=/`, 180-day) that carries the shopper's stored choice —
 * incl. the documented `notOptedOut` field — and is readable SYNCHRONOUSLY in the browser. Because the
 * cookie arrives with the page (before any app JS), reading it at module init gives a returning
 * opted-out shopper's state at first render with NO hydration race — this replaces the earlier
 * `window.__tvConsentDetail` init-event capture hack entirely. The `consentChanged` window event
 * (answer 2.1: fires on every change, and on init on every page load) then keeps the store in sync for
 * IN-SESSION toggles (a shopper opting out without a full reload).
 *
 * SCOPE — server adapters only, NOT ga4. The GA4 dataLayer adapter is deliberately NOT gated here: it
 * only pushes to `window.dataLayer`, and the GTM tags that consume it gate themselves on Google Consent
 * Mode (driven by TrueVault's GTM Custom Template). Under the US opt-out model `analytics_storage` stays
 * GRANTED on a "Do Not Sell or Share" opt-out (only the advertising signals deny), so blocking GA4 in
 * this store would OVER-block first-party analytics that Consent Mode intentionally permits. This store
 * governs the Salesforce server adapters (which send shopper data to Salesforce → must stop on opt-out);
 * GA4/GTM are governed by Consent Mode.
 *
 * It intentionally does NOT go through the SFCC `trackingConsent` (SLAS token) path: when TrueVault is
 * enabled, `config.server.ts` sets `analytics.trackingConsent.enabled = false`, which makes
 * `setTrackingConsent` a no-op and `buildConsentPreferences` return all-granted — so the token route
 * cannot gate the adapters, and re-enabling it would resurrect the built-in banner alongside
 * TrueVault's. See docs/TRUEVAULT-CONSENT-MODE-PLAN.md §5 Q7.
 *
 * SSR SNAPSHOT — always NOT opted out. `getTrueVaultOptedOutServer()` returns `false`: nothing in the
 * server-rendered output depends on the opt-out (PageViewTracker renders `null`; use-analytics gates in
 * effects/handlers, not in render), and the tracking dispatch is client-only, so the client reads the
 * real value synchronously from the first-party cookie before any adapter fires. A per-request server
 * value is deliberately NOT threaded through this module-level store — module state is shared across SSR
 * requests, so a request-specific value here would leak between shoppers. `useSyncExternalStore` handles
 * the server(false)→client(cookie value) transition without a hydration mismatch precisely because the
 * render output is opt-out-independent.
 *
 * VENDOR CONTRACT + FAILURE POSTURE: `notOptedOut` is a DOCUMENTED field (answer 2.2);
 * `analyticsPermitted` mirrors the `consentChanged` detail. The two cookie-read failure modes are
 * handled ASYMMETRICALLY on purpose (see `initialOptedOut`):
 *   • Cookie ABSENT → NOT opted out. A brand-new / undecided visitor is enabled-by-default (US opt-out),
 *     so "tags on until an explicit opt-out arrives" is the correct posture.
 *   • Cookie PRESENT but unparseable / unrecognized shape → FAIL CLOSED (treated as opted out) + a loud
 *     warning. A cookie exists, so this shopper may well have opted out; silently reverting them to
 *     "tracked" would be a CCPA "Do Not Sell or Share" fail-open. We cannot prove they are NOT opted
 *     out, so we stop the Salesforce server adapters and surface the parse failure.
 * The exact cookie VALUE encoding (URL-encoded JSON assumed) and field shape are CONFIRMED IN QA once
 * TrueVault is live (see plan doc §10.3 "wait for live"); until then `isRecognizedDetail` is deliberately
 * strict so an unexpected shape trips the fail-closed path rather than passing silently. The in-session
 * `consentChanged` event is a well-formed Polaris CustomEvent, so `deriveOptedOut` keeps its
 * opt-in-compatible "absent field → not opted out" semantics for THAT path (GDPR regions omit
 * `notOptedOut`); the strict shape check applies only to the untrusted cookie read.
 */

/**
 * Subset of the shopper's consent state this store reads. Shared by the `polaris_consent_settings`
 * cookie payload and the Polaris `consentChanged` event detail. All fields optional — Polaris omits
 * `notOptedOut` outside opt-out jurisdictions, and a malformed cookie/event yields an empty object.
 */
interface ConsentChangedDetail {
    analyticsPermitted?: boolean;
    notOptedOut?: boolean;
}

/** First-party cookie (root domain, path=/, 180-day) carrying the shopper's stored TrueVault choice. */
const CONSENT_COOKIE_NAME = 'polaris_consent_settings';

/**
 * Outcome of reading the first-party consent cookie at init. We keep ABSENT (no cookie — a brand-new /
 * undecided visitor) distinct from UNPARSEABLE (a cookie exists but we can't read the choice out of it)
 * because the two must NOT share a failure posture — see the FAILURE POSTURE note in the docblock and
 * `initialOptedOut`.
 */
type ConsentCookieResult =
    | { status: 'absent' }
    | { status: 'unparseable' }
    | { status: 'parsed'; detail: ConsentChangedDetail };

/**
 * True when a parsed cookie value is a recognizable consent detail: a plain object carrying at least one
 * KNOWN boolean field (`notOptedOut` / `analyticsPermitted`), with any present known field typed as a
 * boolean. Anything else — a bare primitive, `null`, an object with no known field, or `notOptedOut` as
 * a string like `"false"` — is rejected so the caller can fail closed rather than silently mis-read it.
 * Deliberately strict while the exact encoding is unconfirmed; loosen once pinned in QA (plan §10.3).
 */
function isRecognizedDetail(value: unknown): value is ConsentChangedDetail {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as Record<string, unknown>;
    const notOptedOutOk = v.notOptedOut === undefined || typeof v.notOptedOut === 'boolean';
    const analyticsOk = v.analyticsPermitted === undefined || typeof v.analyticsPermitted === 'boolean';
    const hasKnownBoolean = typeof v.notOptedOut === 'boolean' || typeof v.analyticsPermitted === 'boolean';
    return notOptedOutOk && analyticsOk && hasKnownBoolean;
}

/**
 * Read + parse the `polaris_consent_settings` cookie synchronously from `document.cookie`. Returns a
 * discriminated result: `absent` on the server / when the cookie is missing or empty, `unparseable` when
 * a value exists but doesn't decode to a recognized consent shape, or `parsed` with the detail. Assumes
 * the value is URL-encoded JSON (confirmed in QA once TrueVault is live).
 */
function readConsentCookie(): ConsentCookieResult {
    if (typeof document === 'undefined') return { status: 'absent' };
    const prefix = `${CONSENT_COOKIE_NAME}=`;
    const match = document.cookie ? document.cookie.split('; ').find((c) => c.startsWith(prefix)) : undefined;
    if (!match) return { status: 'absent' };
    const raw = match.slice(prefix.length);
    if (!raw) return { status: 'absent' };
    let parsed: unknown;
    try {
        parsed = JSON.parse(decodeURIComponent(raw));
    } catch {
        return { status: 'unparseable' };
    }
    if (!isRecognizedDetail(parsed)) return { status: 'unparseable' };
    return { status: 'parsed', detail: parsed };
}

/**
 * Initial opt-out state, seeded synchronously from the first-party cookie (no hydration race). ABSENT →
 * not opted out (enabled-by-default). PRESENT-but-UNPARSEABLE → FAIL CLOSED (opted out) + warn: a cookie
 * exists but we can't confirm the shopper is NOT opted out, so we stop the server adapters rather than
 * risk a "Do Not Sell or Share" fail-open. See the FAILURE POSTURE note in the docblock.
 */
function initialOptedOut(): boolean {
    const result = readConsentCookie();
    switch (result.status) {
        case 'absent':
            return false;
        case 'unparseable':
            logger.warn(
                'TrueVault: polaris_consent_settings cookie present but unparseable/unrecognized — failing closed (treating shopper as opted out)'
            );
            return true;
        case 'parsed':
            return deriveOptedOut(result.detail);
    }
}

let optedOut = initialOptedOut();
const listeners = new Set<() => void>();
let installed = false;

/**
 * Derive the opt-out flag from a consent detail (cookie payload or `consentChanged` event). The shopper
 * is treated as opted out — so the Salesforce engagement adapters are blocked — when EITHER the CCPA
 * "Do Not Sell or Share" toggle is set (`notOptedOut === false`) OR analytics is explicitly not
 * permitted (`analyticsPermitted === false`). Absent fields (opt-in / GDPR regions, where Polaris omits
 * `notOptedOut`) are NOT treated as opt-out here — GDPR blocking of these adapters is a separate concern
 * this US-only customer doesn't have; see the plan doc.
 */
function deriveOptedOut(detail: ConsentChangedDetail): boolean {
    return detail.notOptedOut === false || detail.analyticsPermitted === false;
}

function handleConsentChanged(event: Event): void {
    const detail = ((event as CustomEvent).detail as ConsentChangedDetail | undefined) ?? {};
    const next = deriveOptedOut(detail);
    if (next !== optedOut) {
        optedOut = next;
        for (const listener of listeners) listener();
    }
}

/** `useSyncExternalStore` subscribe: registers the listener and lazily installs the single window handler. */
export function subscribeTrueVaultConsent(listener: () => void): () => void {
    listeners.add(listener);
    if (!installed && typeof window !== 'undefined') {
        // Listen for IN-SESSION consent changes (a shopper opting out without a full reload). The INITIAL
        // state was already seeded synchronously from the `polaris_consent_settings` cookie at module
        // init, so a returning opted-out shopper is correct at first render without a replay hack.
        window.addEventListener('consentChanged', handleConsentChanged);
        installed = true;
    }
    return () => {
        listeners.delete(listener);
    };
}

/** `useSyncExternalStore` client snapshot: current opt-out state. */
export function getTrueVaultOptedOut(): boolean {
    return optedOut;
}

/** `useSyncExternalStore` server snapshot: enabled-by-default (never opted out during SSR — see docblock). */
export function getTrueVaultOptedOutServer(): boolean {
    return false;
}

/** Test-only reset of module state. Re-reads the cookie so a test can set `document.cookie` first. */
export function __resetTrueVaultConsentStoreForTests(): void {
    listeners.clear();
    installed = false;
    optedOut = initialOptedOut();
}
